import { useRef, useState } from "react";
import { FiUpload, FiX, FiFileText, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { toast } from "react-toastify";
import { Prescription } from "../../../types";
import exercicies from "../../../services/exercicies";
import { extractPdfText } from "../../../helpers/extractPdfText";
import { parseWorkoutPdfText } from "../../../helpers/parseWorkoutPdfText";
import {
  matchImportedExercises,
  MatchStats,
} from "../../../helpers/matchImportedExercises";

type Props = {
  isOpen: boolean;
  professionalId: string;
  onClose: () => void;
  onImport: (payload: {
    planName: string;
    prescriptions: Prescription[];
    stats: MatchStats;
  }) => void | Promise<void>;
};

export default function ImportWorkoutPdfModal({
  isOpen,
  professionalId,
  onClose,
  onImport,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [planName, setPlanName] = useState("");
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [stats, setStats] = useState<MatchStats | null>(null);
  const [error, setError] = useState("");

  if (!isOpen) return null;

  const reset = () => {
    setLoading(false);
    setFileName("");
    setPlanName("");
    setPrescriptions([]);
    setStats(null);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File | null) => {
    if (!file) return;
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Selecione um arquivo PDF.");
      return;
    }

    setLoading(true);
    setError("");
    setFileName(file.name);

    try {
      const text = await extractPdfText(file);
      const parsed = parseWorkoutPdfText(text);
      if (parsed.prescriptions.length === 0) {
        setError(
          "Não encontramos treinos no PDF. Verifique se o arquivo tem Séries/Carga/Intervalo.",
        );
        setPrescriptions([]);
        setStats(null);
        return;
      }

      const catalog = await exercicies.list_all(professionalId, 1, 1000, "");
      const matched = matchImportedExercises(parsed, catalog, professionalId);
      setPlanName(matched.planName);
      setPrescriptions(matched.prescriptions);
      setStats(matched.stats);
    } catch (e) {
      console.error(e);
      setError("Não foi possível ler o PDF. Tente outro arquivo.");
      setPrescriptions([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (prescriptions.length === 0) {
      toast.warning("Nenhum treino para importar.");
      return;
    }
    setLoading(true);
    try {
      await onImport({
        planName,
        prescriptions,
        stats: stats || { total: 0, matched: 0, unmatched: 0 },
      });
      reset();
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-pdf-title"
      data-testid="import-workout-pdf-modal"
    >
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl dark:bg-boxdark sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-stroke px-4 py-3 dark:border-strokedark sm:px-5">
          <h2
            id="import-pdf-title"
            className="text-base font-bold text-gray-900 dark:text-white sm:text-lg"
          >
            Importar treino de outros apps
          </h2>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fechar"
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-meta-4"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Envie o PDF exportado do app (com Rotina, Séries, Carga e Intervalo).
            Os exercícios serão vinculados ao aluno ao salvar.
          </p>

          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            data-testid="import-pdf-input"
            onChange={(e) => void handleFile(e.target.files?.[0] || null)}
          />

          <button
            type="button"
            disabled={loading}
            onClick={() => inputRef.current?.click()}
            className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50 px-4 py-3 text-sm font-semibold text-purple-700 transition hover:border-purple-400 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-300"
          >
            <FiUpload size={18} />
            {loading ? "Processando PDF..." : "Selecionar PDF"}
          </button>

          {fileName ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <FiFileText />
              <span className="truncate">{fileName}</span>
            </div>
          ) : null}

          {error ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
            >
              <FiAlertCircle className="mt-0.5 shrink-0" />
              {error}
            </div>
          ) : null}

          {stats && prescriptions.length > 0 ? (
            <div className="space-y-3 rounded-xl border border-stroke bg-gray-50 p-3 dark:border-strokedark dark:bg-meta-4/40">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Plano
                </p>
                <p className="font-semibold text-gray-900 dark:text-white">
                  {planName}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-purple-100 px-2.5 py-1 font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                  {prescriptions.length} treino
                  {prescriptions.length === 1 ? "" : "s"}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                  <FiCheckCircle size={12} />
                  {stats.matched} no catálogo
                </span>
                {stats.unmatched > 0 ? (
                  <span className="rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                    {stats.unmatched} sem match
                  </span>
                ) : null}
              </div>
              <ul className="max-h-48 space-y-2 overflow-y-auto text-sm">
                {prescriptions.map((p) => (
                  <li key={p.name} className="rounded-lg bg-white px-3 py-2 dark:bg-boxdark">
                    <p className="font-semibold text-gray-800 dark:text-gray-100">
                      {p.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {p.exercises.length} exercício
                      {p.exercises.length === 1 ? "" : "s"}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="flex gap-2 border-t border-stroke px-4 py-3 dark:border-strokedark sm:px-5">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 rounded-xl border border-stroke px-4 py-2.5 text-sm font-semibold text-gray-700 dark:border-strokedark dark:text-gray-200"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={loading || prescriptions.length === 0}
            onClick={() => void handleConfirm()}
            data-testid="import-pdf-confirm"
            className="flex-1 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Importar e salvar
          </button>
        </div>
      </div>
    </div>
  );
}
